import { FormEvent, useEffect, useState } from "react";
import { ApiError } from "../../api/client";
import { useBranchList } from "../../api/branches";
import {
  CreateUserInput,
  ROLE_HINTS,
  ROLE_LABELS,
  UpdateUserInput,
  UserRoleCode,
  UserRow,
  changeUserRole,
  createUser,
  deleteUser,
  setUserActive,
  updateUser,
} from "../../api/users";
import {
  extractPhoneDigits,
  formatPhoneDisplay,
  formatPhoneInput,
  normalizePhoneForApi,
  validateTurkishPhone,
} from "../../utils/phone";
import { normalizeEmailInput, validateEmailInput } from "../../utils/email";
import "./UserFormModal.css";

export function UserFormModal(props: {
  mode: "create" | "edit";
  initial?: UserRow;
  canChooseRole: boolean;
  /** Yeni kayitta secilebilir roller (caller yetkisine gore). */
  allowedCreateRoles?: UserRoleCode[];
  canDeleteUser?: boolean;
  isSelf?: boolean;
  defaultBranchId?: string | null;
  canChooseBranch: boolean;
  showBranchField: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const {
    mode,
    initial,
    canChooseRole,
    allowedCreateRoles,
    canDeleteUser,
    isSelf,
    canChooseBranch,
    showBranchField,
    defaultBranchId,
    onClose,
    onSaved,
  } = props;

  const branches = useBranchList({ isActive: true });

  const [fullName, setFullName] = useState(initial?.full_name ?? "");
  const [email, setEmail] = useState(initial?.email ?? "");
  const [phone, setPhone] = useState(() => formatPhoneDisplay(initial?.phone));
  const [role, setRole] = useState<UserRoleCode>(initial?.role ?? "instructor");
  const [branchId, setBranchId] = useState<string | "">(
    initial?.branch_id ?? defaultBranchId ?? "",
  );
  const [password, setPassword] = useState("");
  const [isActive, setIsActive] = useState(initial?.is_active ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEdit = mode === "edit";
  const requiresBranch = role !== "tenant_admin";

  useEffect(() => {
    setIsActive(initial?.is_active ?? true);
    setRole(initial?.role ?? "instructor");
    setFullName(initial?.full_name ?? "");
    setEmail(initial?.email ?? "");
    setPhone(formatPhoneDisplay(initial?.phone));
    setBranchId(initial?.branch_id ?? defaultBranchId ?? "");
  }, [initial, defaultBranchId]);

  const roleOptions: UserRoleCode[] = canChooseRole
    ? ["tenant_admin", "manager", "office_staff", "instructor", "student"]
    : allowedCreateRoles ?? ["instructor", "student"];

  // Rol degisikligi yalnizca kurum yoneticisinde; sube muduru / buro personeli
  // yeni kayitta kendi yetkisindeki rolleri secebilir.
  const roleSelectDisabled = isEdit
    ? busy || !canChooseRole || Boolean(isSelf)
    : busy || roleOptions.length < 2;

  const handleDelete = async () => {
    if (!initial || !canDeleteUser) return;
    const confirmed = window.confirm(
      `"${initial.full_name}" kullanicisi kalici olarak silinecek.\n\nBu islem geri alinamaz. Emin misiniz?`,
    );
    if (!confirmed) return;
    setBusy(true);
    setError(null);
    try {
      await deleteUser(initial.id);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Kullanici silinemedi.");
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const phoneDigits = extractPhoneDigits(phone);
    const phoneErr = validateTurkishPhone(phoneDigits);
    if (phoneErr) {
      setError(phoneErr);
      setBusy(false);
      return;
    }
    const phoneNormalized = phoneDigits ? normalizePhoneForApi(phone) : null;
    const emailNormalized = normalizeEmailInput(email);
    const emailErr = validateEmailInput(emailNormalized);
    if (emailErr) {
      setError(emailErr);
      setBusy(false);
      return;
    }
    const effectiveBranchId = showBranchField
      ? branchId || null
      : initial?.branch_id ?? defaultBranchId ?? null;
    try {
      if (isEdit && initial) {
        const body: UpdateUserInput = {
          fullName: fullName.trim(),
          phone: phoneNormalized,
          branchId: effectiveBranchId,
        };
        if (canChooseRole) {
          body.email = emailNormalized;
        }
        await updateUser(initial.id, body);
        if (isActive !== initial.is_active) {
          await setUserActive(initial.id, isActive);
        }
        if (canChooseRole && !isSelf && role !== initial.role) {
          await changeUserRole(initial.id, role);
        }
      } else {
        const body: CreateUserInput = {
          fullName: fullName.trim(),
          email: emailNormalized,
          phone: phoneNormalized,
          role,
          branchId: effectiveBranchId,
          password,
        };
        await createUser(body);
      }
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Kayit Başarısız.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="kp-user-modal-backdrop" role="dialog" aria-modal="true">
      <div className="kp-user-modal">
        <header className="kp-user-modal__header">
          <h3>{isEdit ? "Kullanıcı Düzenle" : "Yeni Kullanıcı"}</h3>
          <button className="kp-user-modal__close" onClick={onClose}>x</button>
        </header>
        <form onSubmit={onSubmit} className="kp-user-modal__body">
          <label className="kp-user-field">
            <span>Ad Soyad <em>*</em></span>
            <input
              type="text"
              maxLength={150}
              required
              value={fullName}
              disabled={busy}
              onChange={(e) => setFullName(e.target.value)}
            />
          </label>

          <label className="kp-user-field">
            <span>E-posta <em>*</em></span>
            <input
              type="text"
              inputMode="email"
              autoComplete="email"
              maxLength={255}
              required
              value={email}
              disabled={busy || (isEdit && !canChooseRole)}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setEmail(normalizeEmailInput(email))}
            />
            <small className="kp-muted">
              Latin harf kullanin. Turkce karakter (ı, ğ, ü, ş…) otomatik duzeltilir.
            </small>
            {isEdit && canChooseRole && (
              <small className="kp-muted">
                Giris adresi. Hatali e-posta duzeltilirse kullanici yeni adresle giris yapabilir.
              </small>
            )}
            {isEdit && !canChooseRole && (
              <small className="kp-muted">E-posta yalnizca kurum yoneticisi tarafindan degistirilebilir.</small>
            )}
          </label>

          <div className="kp-user-modal__row">
            <label className="kp-user-field">
              <span>Telefon</span>
              <input
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                maxLength={16}
                value={phone}
                disabled={busy}
                placeholder="0555 685 75 80"
                onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
                onBlur={() => {
                  const digits = extractPhoneDigits(phone);
                  if (digits) setPhone(formatPhoneDisplay(digits));
                }}
              />
            </label>

            {(canChooseRole || !isEdit) && (
              <label className="kp-user-field">
                <span>Rol <em>*</em></span>
                <select
                  className="kp-input"
                  value={role}
                  disabled={roleSelectDisabled}
                  onChange={(e) => setRole(e.target.value as UserRoleCode)}
                >
                  {roleOptions.map((r) => (
                    <option key={r} value={r}>
                      {ROLE_LABELS[r]}
                    </option>
                  ))}
                </select>
                <small className="kp-muted">{ROLE_HINTS[role]}</small>
                {isEdit && isSelf && (
                  <small className="kp-muted">Kendi rolunuzu bu ekrandan degistiremezsiniz.</small>
                )}
              </label>
            )}
          </div>

          {showBranchField && (
            <label className="kp-user-field">
              <span>
                Şube {requiresBranch ? <em>*</em> : <span className="kp-muted"> (kurum yöneticisi için opsiyonel)</span>}
              </span>
              <select
                className="kp-input"
                value={branchId}
                disabled={busy || !canChooseBranch}
                onChange={(e) => setBranchId(e.target.value)}
              >
                {!requiresBranch && <option value="">Şube Seçilmedi</option>}
                {requiresBranch && branchId === "" && <option value="">Şube Seçin</option>}
                {(branches.data || []).map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
              {!canChooseBranch && (
                <small className="kp-muted">Yalnizca kendi subenize ekleyebilirsiniz.</small>
              )}
              {!isEdit && role === "tenant_admin" && branchId && (
                <small className="kp-muted">
                  Kurum yöneticisi şube seçse bile tüm şubelerin verisini görür. Şube müdürü için &quot;Şube Müdürü&quot; rolünü seçin.
                </small>
              )}
            </label>
          )}

          {!isEdit && (
            <label className="kp-user-field">
              <span>Baslangic Parolasi <em>*</em></span>
              <input
                type="text"
                value={password}
                disabled={busy}
                onChange={(e) => setPassword(e.target.value)}
                minLength={8}
                maxLength={128}
                placeholder="En az 8 karakter, harf + rakam"
                required
              />
              <small className="kp-muted">
                Kullanıcıya bu parolayi iletin; ilk girisinde Profil &gt; Şifre Degistir'den
                Güncelleyebilir.
              </small>
            </label>
          )}

          {isEdit && (
            <div className="kp-user-field kp-form-status">
              <span>Durum</span>
              <label className="kp-form-status__toggle">
                <input
                  type="checkbox"
                  checked={isActive}
                  disabled={busy}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
                <span>{isActive ? "Aktif kullanici" : "Pasif kullanici"}</span>
              </label>
              <small className="kp-form-status__hint">Pasif kullanicilar sisteme giris yapamaz.</small>
            </div>
          )}

          {error && <div className="kp-user-modal__error">{error}</div>}

          {isEdit && canDeleteUser && (
            <div className="kp-user-modal__danger">
              <div>
                <strong>Kalici silme</strong>
                <p className="kp-muted">
                  Kullanicinin sistem kaydi tamamen kaldirilir. Bagli randevu, denetim veya egitmen/kursiyer
                  kaydi varsa silme engellenir.
                </p>
              </div>
              <button
                type="button"
                className="kp-btn kp-btn--danger"
                onClick={handleDelete}
                disabled={busy}
              >
                Kalici Sil
              </button>
            </div>
          )}

          <div className="kp-user-modal__actions">
            <button type="button" className="kp-btn" onClick={onClose} disabled={busy}>
              Vazgeç
            </button>
            <button
              type="submit"
              className="kp-btn kp-btn--primary"
              disabled={
                busy ||
                !fullName.trim() ||
                !email.trim() ||
                (!isEdit && !password) ||
                (showBranchField && requiresBranch && !branchId)
              }
            >
              {busy ? "Kaydediliyor…" : isEdit ? "Güncelle" : "Oluştur"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
